import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Save, Loader2, Check, Plus, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

const DISPLAY_TOGGLES = [
  { key: 'variantNoteProducts', label: 'Products page', desc: 'Product list rows' },
  { key: 'variantNoteFactory', label: 'Factory', desc: 'Preset dropdown and name preview' },
  { key: 'variantNoteStock', label: 'Stock page', desc: 'Finished product stock rows' },
  { key: 'variantNoteInvoices', label: 'Invoices page', desc: 'Product picker and invoice line items' },
  { key: 'variantNoteInvoicePdf', label: 'Invoice PDF (Fatura)', desc: 'Printed product lines — new PDFs only, shipped archives stay frozen' },
];

const FIELDS = [
  { key: 'name', label: 'Emri i Kompanisë (Company Name)', placeholder: 'SUTEKO STEEL SH.P.K.' },
  { key: 'nipt', label: 'NUI (Numri Unik Identifikues)', placeholder: '812278473' },
  { key: 'address', label: 'Adresa (Address)', placeholder: 'Magjistrale Prishtine Ferizaj p.n' },
  { key: 'phone1', label: 'Telefon 1 (Phone 1)', placeholder: '046-364-444' },
  { key: 'phone2', label: 'Telefon 2 (Phone 2)', placeholder: '044-846-357' },
  { key: 'bankName', label: 'Banka (Bank Name)', placeholder: 'Banka Kombetare Tregtare (BKT)' },
  { key: 'iban', label: 'IBAN', placeholder: 'XK051940017037041104' },
];

const SettingsPage = () => {
  const [seller, setSeller] = useState({});
  const [operations, setOperations] = useState({ perTonOverheadEur: 0, overheadSchedule: [] });
  const [display, setDisplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOps, setSavingOps] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [opsSavedAt, setOpsSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Give schedule rows stable keys so React state survives row removal
  const keyedOps = (ops) => ({
    ...ops,
    overheadSchedule: (ops.overheadSchedule || []).map((e) => ({ _k: Math.random().toString(36).slice(2), ...e })),
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [sellerRes, opsRes, dispRes] = await Promise.all([
          fetch(`${API_URL}/api/settings/seller`),
          fetch(`${API_URL}/api/settings/operations`),
          fetch(`${API_URL}/api/settings/display`),
        ]);
        if (sellerRes.ok) setSeller(await sellerRes.json());
        if (opsRes.ok) setOperations(keyedOps(await opsRes.json()));
        if (dispRes.ok) setDisplay(await dispRes.json());
      } catch (e) {
        setError('Could not load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_URL]);

  const updateField = (key, value) => {
    setSeller({ ...seller, [key]: value });
    setSavedAt(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/settings/seller`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seller),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setSeller(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOps = async () => {
    setSavingOps(true);
    setError(null);
    try {
      const body = {
        perTonOverheadEur: parseFloat(operations.perTonOverheadEur) || 0,
        overheadSchedule: (operations.overheadSchedule || [])
          .filter((e) => e.fromMonth)
          .map((e) => ({ fromMonth: e.fromMonth, monthlyOverheadEur: parseFloat(e.monthlyOverheadEur) || 0 })),
      };
      const res = await fetch(`${API_URL}/api/settings/operations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      setOperations(keyedOps(await res.json()));
      setOpsSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSavingOps(false);
    }
  };

  const updateScheduleRow = (i, key, value) => {
    const rows = [...(operations.overheadSchedule || [])];
    rows[i] = { ...rows[i], [key]: value };
    setOperations({ ...operations, overheadSchedule: rows });
    setOpsSavedAt(null);
  };

  const addScheduleRow = () => {
    setOperations({
      ...operations,
      overheadSchedule: [...(operations.overheadSchedule || []), { _k: Math.random().toString(36).slice(2), fromMonth: '', monthlyOverheadEur: '' }],
    });
    setOpsSavedAt(null);
  };

  const removeScheduleRow = (i) => {
    setOperations({
      ...operations,
      overheadSchedule: (operations.overheadSchedule || []).filter((_, idx) => idx !== i),
    });
    setOpsSavedAt(null);
  };

  // Variant note visibility — saves immediately on toggle
  const toggleDisplay = async (key) => {
    const next = { ...display, [key]: !display?.[key] };
    setDisplay(next);
    try {
      const res = await fetch(`${API_URL}/api/settings/display`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: next[key] }),
      });
      if (res.ok) setDisplay(await res.json());
    } catch (e) {
      setError('Could not save display setting');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center" data-testid="settings-loading">
        <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-2">
        <SettingsIcon className="w-6 h-6 text-zinc-400" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Settings</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        These details appear on every outgoing Fatura PDF.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-rose-950/40 border border-rose-900/50 rounded-lg text-sm text-rose-300" data-testid="settings-error">
          {error}
        </div>
      )}

      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
          Të dhënat e Shitësit (Seller details)
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className={key === 'address' || key === 'name' ? 'sm:col-span-2' : ''}>
              <Label className="text-zinc-400 text-sm">{label}</Label>
              <Input
                type="text"
                value={seller[key] || ''}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={placeholder}
                className="bg-zinc-800 border-zinc-700 text-zinc-200"
                data-testid={`seller-${key}`}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-800">
          {savedAt && (
            <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="settings-saved-indicator">
              <Check className="w-3.5 h-3.5" />
              Saved {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Variant note visibility */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-5 space-y-4 mt-6" data-testid="display-card">
        <div>
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
            Variant Note Visibility
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Choose where the product's variant note (e.g. "No Lip") is shown next to the name.
            Analytics and the Purchase Planner always count variants together as one product.
          </p>
        </div>
        <div className="divide-y divide-zinc-800/60">
          {DISPLAY_TOGGLES.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-2.5 gap-4">
              <div>
                <div className="text-sm text-zinc-200">{label}</div>
                <div className="text-xs text-zinc-500">{desc}</div>
              </div>
              <Switch
                checked={!!display?.[key]}
                onCheckedChange={() => toggleDisplay(key)}
                data-testid={`display-toggle-${key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Operations / Overhead — drives Analytics dashboard */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-5 space-y-4 mt-6" data-testid="operations-card">
        <div>
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
            Operations &amp; Overhead
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            These figures are subtracted from gross profit in the Analytics dashboard to give you true net profit.
            Leave at 0 if you don't want to factor them in yet.
          </p>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-sm">Monthly fixed overhead (€/month)</Label>
            <p className="text-[10px] text-zinc-500 mt-1">
              Each entry applies from the selected month until the next entry. Months before the first entry are treated as zero.
            </p>
          </div>
          {(operations.overheadSchedule || []).map((e, i) => (
            <div key={e._k || i} className="flex items-center gap-2" data-testid={`ops-schedule-row-${i}`}>
              <span className="text-xs text-zinc-500 w-10">From</span>
              <Input
                type="month"
                value={e.fromMonth || ''}
                onChange={(ev) => updateScheduleRow(i, 'fromMonth', ev.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-200 w-44"
                data-testid={`ops-schedule-month-${i}`}
              />
              <Input
                type="number"
                step="0.01"
                value={e.monthlyOverheadEur ?? ''}
                onChange={(ev) => updateScheduleRow(i, 'monthlyOverheadEur', ev.target.value)}
                placeholder="0.00"
                className="bg-zinc-800 border-zinc-700 text-zinc-200 w-32"
                data-testid={`ops-schedule-amount-${i}`}
              />
              <span className="text-xs text-zinc-500">€/mo</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeScheduleRow(i)}
                className="text-zinc-500 hover:text-rose-400 h-8 px-2"
                data-testid={`ops-schedule-remove-${i}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addScheduleRow}
            className="border-zinc-700 text-zinc-300 gap-1.5 h-8 text-xs"
            data-testid="ops-schedule-add"
          >
            <Plus className="w-3.5 h-3.5" /> Add rate change
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-400 text-sm">Per-ton overhead (€/ton sold)</Label>
            <Input
              type="number"
              step="0.01"
              value={operations.perTonOverheadEur ?? 0}
              onChange={(e) => { setOperations({ ...operations, perTonOverheadEur: e.target.value }); setOpsSavedAt(null); }}
              placeholder="0.00"
              className="bg-zinc-800 border-zinc-700 text-zinc-200"
              data-testid="ops-per-ton-overhead"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Electricity etc. Applied per metric ton of finished goods sold.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-800">
          {opsSavedAt && (
            <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="ops-saved-indicator">
              <Check className="w-3.5 h-3.5" />
              Saved {opsSavedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button
            onClick={handleSaveOps}
            disabled={savingOps}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="ops-save-btn"
          >
            {savingOps ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {savingOps ? 'Saving…' : 'Save overhead'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
